import { Injectable, isDevMode, signal, computed } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

import { OverlayContainerContextService } from './overlay-container-context.service';

const CONTAINER_CLASS = 'cdk-overlay-container';
const OVERLAY_ANCHOR_ID = 'cdk-overlay-anchor';

/** Where MatDialog / CDK overlays attach while a dialog scope is active. */
export type DialogOverlayTarget = 'body' | 'scoped';

/**
 * Custom OverlayContainer that switches attachment based on whether a drawer is open,
 * and supports scoping overlays to a specific feature element.
 *
 * **Why this exists**: We need the top bar and global-client-dropdown to stay above
 * dialogs/overlays (so they attach inside the layout under #cdk-overlay-anchor), but
 * dropdowns opened inside a jep-drawer (e.g. Status filter) must appear above the
 * drawer—which works best when those overlays attach to body. A single provider
 * cannot be "anchor only for topbar" and "body for drawer" without context; this
 * service provides that context by maintaining two containers and delegating to
 * OverlayContainerContextService.
 *
 * **Scoped container**: Feature routes can call `setScopedContainer(element)` so a
 * dedicated `.cdk-overlay-container` node exists under the host (e.g. dialog-host).
 * While a dialog session is active (`activateDialogScope`), `getContainerElement()`
 * normally returns the **body** container: the scoped parent uses `transform: translateZ(0)`,
 * which breaks nested `cdkConnectedOverlay` / `FlexibleConnectedPositionStrategy`
 * (viewport vs fixed containing block). Complex dialogs (e.g. Report Builder with
 * pickers) should use the default **`activateDialogScope()`** → **`'body'`**.
 *
 * For **simple** modals that need to center in the main content (not the viewport),
 * call **`activateDialogScope('scoped')`**: overlays attach to the scoped node under
 * the host so CDK centers within that region. Pair each call with `deactivateDialogScope()`.
 *
 * **How it works**: We keep two container elements (anchor + body). getContainerElement()
 * returns the anchor container when no drawer is open (top bar stays on top) and the
 * body container when any drawer is open (drawer dropdowns layer correctly). The
 * context is updated by jep-drawer on open/close. See also OverlayContainerContextService
 * and CLAUDE.md (Overlay and drawer layering).
 */
@Injectable()
export class InAppOverlayContainerService extends OverlayContainer {
	private anchorContainer: HTMLElement | undefined;
	private bodyContainer: HTMLElement | undefined;

	private _scopedContainer: HTMLElement | undefined;
	private _scopedParent: HTMLElement | null = null;

	/** Tracks MatDialog sessions opened after `activateDialogScope()` (paired with `deactivateDialogScope()`). */
	private readonly _activeDialogCount = signal(0);

	/**
	 * Parallel stack: overlay target for each active dialog scope (LIFO with deactivate).
	 */
	private readonly _dialogOverlayTargets: DialogOverlayTarget[] = [];

	/** True whenever at least one scoped dialog is open on the dialog host. */
	readonly hasActiveDialog = computed(() => this._activeDialogCount() > 0);

	/**
	 * Call before MatDialog.open(). Defaults to `'body'` so nested CDK overlays
	 * (menus, color pickers) position correctly. Use `'scoped'` for simple modals
	 * that should center in the dialog-host content area.
	 */
	activateDialogScope(overlayTarget: DialogOverlayTarget = 'body'): void {
		this._dialogOverlayTargets.push(overlayTarget);
		this._activeDialogCount.update(n => n + 1);
	}

	/** Call in each dialog's afterClosed() to pop the matching activateDialogScope(). */
	deactivateDialogScope(): void {
		if (this._activeDialogCount() > 0) {
			this._dialogOverlayTargets.pop();
			this._activeDialogCount.update(n => n - 1);
		} else if (isDevMode()) {
			console.warn(
				'InAppOverlayContainerService: deactivateDialogScope() called without a matching activateDialogScope().'
			);
		}
	}

	constructor(private overlayContext: OverlayContainerContextService) {
		super();
	}

	setScopedContainer(element: HTMLElement | null): void {
		this._scopedParent = element;

		if (this._scopedContainer) {
			this._scopedContainer.remove();
			this._scopedContainer = undefined;
		}

		if (element) {
			this._scopedContainer = this._createContainerElement(element);
		}
	}

	override getContainerElement(): HTMLElement {
		this._loadStyles();
		this._ensureContainers();

		if (
			this._scopedContainer &&
			this._scopedParent?.isConnected &&
			this._activeDialogCount() > 0
		) {
			const target =
				this._dialogOverlayTargets[this._dialogOverlayTargets.length - 1] ??
				'body';
			if (target === 'scoped') {
				return this._scopedContainer;
			}
			// Nested overlays (cdkConnectedOverlay, mat-menu, etc.) must attach to the
			// body container: the scoped node lives under dialog-host with
			// transform: translateZ(0), so position:fixed uses a different containing
			// block than FlexibleConnectedPositionStrategy’s viewport math — panels
			// appear offset (see dialog-host mtx-colorpicker workaround and CLAUDE.md).
			return this.bodyContainer!;
		}

		return this.overlayContext.useBodyContainer()
			? this.bodyContainer!
			: this.anchorContainer!;
	}

	private _ensureContainers(): void {
		if (this._platform.isBrowser) {
			this._cleanupOppositePlatform();
			if (!this.anchorContainer) {
				this.anchorContainer = this._createContainerElement(
					this._document.getElementById(OVERLAY_ANCHOR_ID) ??
						this._document.body
				);
			}
			if (!this.bodyContainer) {
				this.bodyContainer = this._createContainerElement(
					this._document.body
				);
			}
			// Recreate anchor container if detached (e.g. layout change removed anchor)
			if (
				this.anchorContainer &&
				!this._document.body.contains(this.anchorContainer)
			) {
				this.anchorContainer = this._createContainerElement(
					this._document.getElementById(OVERLAY_ANCHOR_ID) ??
						this._document.body
				);
			}
		} else {
			this.anchorContainer ??= this._createContainerElement(
				this._document.body
			);
			this.bodyContainer ??= this.anchorContainer;
		}
	}

	private _cleanupOppositePlatform(): void {
		const opposite = this._document.querySelectorAll(
			`.${CONTAINER_CLASS}[platform="server"], .${CONTAINER_CLASS}[platform="test"]`
		);
		opposite.forEach(el => el.remove());
	}

	private _createContainerElement(parent: HTMLElement): HTMLElement {
		const container = this._document.createElement('div');
		container.classList.add(CONTAINER_CLASS);
		if (!this._platform.isBrowser) {
			container.setAttribute('platform', 'server');
		}
		parent.appendChild(container);
		return container;
	}
}
